import React, { useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import authService from '../../services/authService';
import { jwtDecode } from 'jwt-decode';

/**
 * Page de connexion unifiee (Coach + Client)
 * Route: / (racine)
 *
 * Detecte automatiquement si l'utilisateur est un coach ou un client
 * et redirige vers l'espace approprie
 */
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowAlert(false);

    try {
      // Utiliser le login unifie qui detecte coach ou client
      const data = await authService.unifiedLogin(email, password);
      login(data.token);

      // Rediriger selon le type d'utilisateur
      if (data.userType === 'client') {
        // Client -> espace client
        window.location.href = '/client';
      } else {
        // Coach -> dashboard (tous les coachs ont au moins le plan free)
        window.location.href = '/coach';
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);

      if (error.message.includes('non trouvé') || error.message.includes('not found')) {
        setErrorMessage('Compte non trouve. Verifiez votre email.');
      } else if (error.message.includes('incorrect') || error.message.includes('Invalid')) {
        setErrorMessage('Email ou mot de passe incorrect.');
      } else if (error.message.includes('suspended')) {
        setErrorMessage('Votre compte est suspendu. Contactez le support.');
      } else if (error.message.includes('Portal access')) {
        setErrorMessage('Acces non active. Contactez votre coach.');
      } else if (error.message.includes('No password set')) {
        setErrorMessage('Mot de passe non configure. Contactez votre coach.');
      } else {
        setErrorMessage(error.message || 'Erreur de connexion.');
      }
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Connexion - Yoco</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'white',
        }}
      >
        <Container maxWidth="xs">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 4,
              borderRadius: 2,
              bgcolor: 'white',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/static/logo_black.svg"
              alt="Yoco"
              sx={{ height: 40, mb: 4 }}
            />

            {/* Message d'erreur */}
            <Collapse in={showAlert} sx={{ width: '100%' }}>
              <Alert
                severity="error"
                onClose={() => setShowAlert(false)}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            </Collapse>

            {/* Formulaire */}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                size="small"
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(prev => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, py: 1.5 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Se connecter'}
              </Button>

              {/* Lien mot de passe oublié */}
              <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">
                    Mot de passe oublié ?
                  </Typography>
                </Link>
              </Stack>

              {/* Lien inscription */}
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Nouveau coach ?
                </Typography>
              </Divider>

              <Button
                component={Link}
                to="/register"
                fullWidth
                variant="outlined"
                sx={{ py: 1.5 }}
              >
                Créer un compte
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Login;
